import React, { FC, PropsWithChildren } from 'react'
import format from 'date-fns/format'
import { Sensor, SensorState } from 'src/api'
import { TabGroupFilter, PropsInjector, DataLoad } from 'src/components'
import { useSensorListRequest } from 'src/hooks/api'
import { useQueryParamsState } from 'src/hooks/ui'
import { transformSensorsState, transformBoolean as transformKeyAndValue } from 'src/hooks/api/helpers'
import { SensorStateList, SearchParamsKeys } from 'src/enums'
import {
  AllLoRaWanSensorList,
  NewLoRaWanSensorList,
  AvailableLoRaWanSensorList,
  AssignedLoRaWanSensorList,
  InUseLoRaWanSensorList,
  LostLoRaWanSensorList
} from './sensorList/lorawan'

const getStatusList = ({
  data,
  count,
  isLoading,
  states,
  refetchSensorList
}: {
  data: Sensor[]
  count: number
  isLoading: boolean
  states: SensorState[]
  refetchSensorList: () => void
}) => [
  {
    status: SensorStateList.All,
    content: <AllLoRaWanSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.New,
    content: <NewLoRaWanSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Available,
    content: <AvailableLoRaWanSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Assigned,
    content: <AssignedLoRaWanSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.InUse,
    content: <InUseLoRaWanSensorList data={data} count={count} isLoading={isLoading} />
  },
  {
    status: SensorStateList.Lost,
    content: <LostLoRaWanSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  }
]

interface LoRaWanSensorTabsProps {
  data?: Sensor[]
  count?: number
  isLoading?: boolean
  refetchSensorList?: () => void
  states?: SensorState[]
}

const LoRaWanSensorTabs: FC<LoRaWanSensorTabsProps> = ({ data = [], count = 0, isLoading = false, refetchSensorList = () => {}, states = [] }) => {
  return (
    <TabGroupFilter
      statusList={getStatusList({
        data,
        count,
        isLoading,
        refetchSensorList,
        states
      })}
      name={SearchParamsKeys.State}
      paramsToRemove={[SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage, SearchParamsKeys.Sort]}
    />
  )
}

interface LoRaWanSensorListLoaderProps {
  states: SensorState[]
  typeId: number
}

const LoRaWanSensorListLoader: FC<PropsWithChildren<LoRaWanSensorListLoaderProps>> = ({ states, typeId, children }) => {
  const state = useQueryParamsState()

  const { data, error, isInitialLoading, isError, refetch } = useSensorListRequest({
    params: {
      ...state,
      typeId
    },
    paramsTransform: [
      transformSensorsState(states || []),
      transformKeyAndValue(SearchParamsKeys.LowBattery, SearchParamsKeys.BatteryLevel, 25),
      transformKeyAndValue(SearchParamsKeys.WeakSignal, SearchParamsKeys.SignalStrength, -100),
      transformKeyAndValue(
        SearchParamsKeys.LastPingMoreThanHour,
        SearchParamsKeys.LastPing,
        JSON.stringify({
          lt: format(new Date(new Date().setHours(new Date().getHours() - 1)), "yyyy-MM-dd'T'HH:mm:ssxxx")
        })
      )
    ]
  })

  const props = {
    data: data?.rows || [],
    count: data?.count || 0,
    isLoading: isInitialLoading,
    states,
    refetchSensorList: refetch
  }

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

interface LoRaWanSensorListProps {
  states?: SensorState[]
  typeId?: number
}

export const LoRaWanSensorList: FC<LoRaWanSensorListProps> = ({ states = [], typeId }) => {
  if (!typeId) {
    return null
  }

  return (
    <LoRaWanSensorListLoader states={states} typeId={typeId}>
      <LoRaWanSensorTabs />
    </LoRaWanSensorListLoader>
  )
}
